'use client';

import * as React from 'react';
import { DataTable } from '@/components/data-table/data-table';
import { useDataTable } from '@/hooks/use-data-table';
import { DataTableSortList } from '@/components/data-table/data-table-sort-list';

import { useOffersTableColumns } from './offers-table-columns';
import { OffersTableActions } from './OffersTableActions';
import { OffersTableActionBar } from './OffersTableActionBar';
import { OffersTableSkeleton } from './OffersTableSkeleton';
import { MainTableState } from '@/lib/utils/table-state-utils';
import type { TableConfig } from '@/types/data-table';
import { DEFAULT_SKELETON_ROWS } from '../../../../../../config/data-table';
import { DeleteOfferModal } from './delete-offer-modal';
import { OfferItem } from '@/lib/api/offers';
import { useStoreNavigate } from '@/hooks/use-app-navigate';

interface OffersTableProps {
    offers: OfferItem[];
    total: number;
    loading: boolean;
    onTableStateChange: (state: MainTableState) => void;
    onRefetch: () => void;
    config?: TableConfig;
}

/**
 * OffersTable component for displaying offers with DataTable functionality
 */
export function OffersTable({
    offers,
    total,
    loading,
    onTableStateChange,
    onRefetch,
    config = {},
}: OffersTableProps) {
    const { storeRoutes, navigate, id: storeId } = useStoreNavigate();

    const [offerId, setOfferId] = React.useState<string | undefined>(undefined)

    const columns = useOffersTableColumns({
        storeId: storeId,
        onEditOffer: (id: string) => navigate(storeRoutes.OFFER_EDIT(id)),
        onDeleteOffer: (id: string) => setOfferId(id),
    })

    const { table } = useDataTable({
        data: offers,
        columns,
        total,
        config: {
            ...config,
            clientMode: true,
            defaultSorting: [{ id: 'createdAt', desc: true }],
        },
        onTableStateChange,
        getRowId: (row: OfferItem) => row.id,
    });

    return (
        <DataTable
            table={table}
            actionBar={<OffersTableActionBar table={table} storeId={storeId} />}
            isLoading={loading}
            skeletonRows={<OffersTableSkeleton rows={config.skeletonRows || DEFAULT_SKELETON_ROWS} />}
        >
            <OffersTableActions table={table} onRefresh={onRefetch} isLoading={loading} storeId={storeId} />
            <DataTableSortList table={table} align="end" />
            <DeleteOfferModal offerId={offerId} storeId={storeId} onClose={() => setOfferId(undefined)} />
        </DataTable>
    );
}
